'use client';

import { useEffect, useLayoutEffect, useRef, type RefObject } from 'react';

interface Layer {
  panel: HTMLElement;
  root: HTMLElement;
  previousFocus: HTMLElement | null;
  preferredFocus: HTMLElement | null;
  focusableSelector: string;
}

interface ModalState {
  layers: Layer[];
}

const modalStates = new WeakMap<Document, ModalState>();

function topLayer(state: ModalState): Layer | undefined {
  const doc = state.layers[0]?.panel.ownerDocument;
  if (!doc) return;
  // Match DispatchShellChrome's rendered-stacking contract, including its
  // shells and portalled Popover dialogs. Registration order is not paint order.
  const roots = Array.from(
    doc.querySelectorAll<HTMLElement>(
      '[data-dispatch-layer], [data-popover-layer], [role="dialog"], [data-modal-layer]',
    ),
  ).filter(visible);
  const top = topRoot(roots);
  return state.layers.find((layer) => layer.root === top);
}

function topModalLayer(state: ModalState): Layer | undefined {
  // Popovers participate in keyboard ownership, but never modal restoration.
  const top = topRoot(
    state.layers
      .filter((layer) => layer.root.isConnected && visible(layer.root))
      .map((layer) => layer.root),
  );
  return state.layers.find((layer) => layer.root === top);
}

function topRoot(roots: HTMLElement[]): HTMLElement | undefined {
  return roots.reduce<HTMLElement | undefined>((highest, candidate) => {
    if (!highest) return candidate;
    const candidateHeight = layerHeight(candidate);
    const highestHeight = layerHeight(highest);
    if (candidateHeight !== highestHeight)
      return candidateHeight > highestHeight ? candidate : highest;
    return highest.compareDocumentPosition(candidate) & Node.DOCUMENT_POSITION_FOLLOWING
      ? candidate
      : highest;
  }, undefined);
}

function layerHeight(root: HTMLElement): number {
  let height = 0;
  for (let node: HTMLElement | null = root; node; node = node.parentElement) {
    height = Math.max(height, Number.parseInt(window.getComputedStyle(node).zIndex, 10) || 0);
  }
  return height;
}

function visible(element: HTMLElement): boolean {
  for (let node: HTMLElement | null = element; node; node = node.parentElement) {
    if (node.hidden || node.hasAttribute('inert') || node.getAttribute('aria-hidden') === 'true')
      return false;
    const style = window.getComputedStyle(node);
    if (style.display === 'none' || style.visibility === 'hidden') return false;
  }
  return true;
}

function canFocus(element: HTMLElement): boolean {
  return !element.matches('input[type="hidden"], [disabled], :disabled') && visible(element);
}

function focusInside(layer: Layer, saved: HTMLElement | null) {
  const target =
    saved?.isConnected && layer.panel.contains(saved) && canFocus(saved)
      ? saved
      : layer.preferredFocus?.isConnected &&
          layer.panel.contains(layer.preferredFocus) &&
          canFocus(layer.preferredFocus)
        ? layer.preferredFocus
        : Array.from(layer.panel.querySelectorAll<HTMLElement>(layer.focusableSelector)).find(
            (element) => element.tabIndex >= 0 && canFocus(element),
          );
  target?.focus();
  if (!target || layer.panel.ownerDocument.activeElement !== target) {
    if (!layer.panel.hasAttribute('tabindex')) layer.panel.tabIndex = -1;
    layer.panel.focus();
  }
}

function ensureScrollLockStyle(doc: Document) {
  if (doc.getElementById('statura-modal-scroll-lock')) return;
  const style = doc.createElement('style');
  style.id = 'statura-modal-scroll-lock';
  // The attribute owns only our lock. Never read or write a foreign inline
  // overflow value; important also beats normal inline values while locked.
  style.textContent = 'body[data-modal-scroll-lock] { overflow: hidden !important; }';
  doc.head.appendChild(style);
}

/** Shared stack and attribute-based scroll ownership for Dialog and Sheet. */
export function useModalLayer({
  open,
  panelRef,
  rootRef,
  preferredFocusRef,
  onClose,
  closeOnEscape,
  focusableSelector,
}: {
  open: boolean;
  panelRef: RefObject<HTMLDivElement | null>;
  /** Defaults to the panel parent; use the panel for a self-rooted floating dialog. */
  rootRef?: RefObject<HTMLElement | null>;
  /** Optional safe restoration target inside a surviving modal. */
  preferredFocusRef?: RefObject<HTMLElement | null>;
  onClose: () => void;
  closeOnEscape: boolean;
  focusableSelector: string;
}) {
  const callbacks = useRef({ onClose, closeOnEscape });
  useEffect(() => {
    callbacks.current = { onClose, closeOnEscape };
  }, [onClose, closeOnEscape]);

  useLayoutEffect(() => {
    const panel = panelRef.current;
    if (!open || !panel) return;
    const doc = panel.ownerDocument;
    const body = doc.body;
    let state = modalStates.get(doc);
    if (!state) {
      state = { layers: [] };
      modalStates.set(doc, state);
    }
    const layer: Layer = {
      panel,
      root: rootRef?.current ?? panel.parentElement!,
      focusableSelector,
      previousFocus: doc.activeElement instanceof HTMLElement ? doc.activeElement : null,
      preferredFocus: preferredFocusRef?.current ?? null,
    };
    state.layers.push(layer);
    ensureScrollLockStyle(doc);
    body.setAttribute('data-modal-scroll-lock', '');

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented || topLayer(state) !== layer) return;
      if (event.key === 'Escape') {
        // Consume the key even when this layer disallows Escape dismissal.
        event.stopImmediatePropagation();
        if (callbacks.current.closeOnEscape) callbacks.current.onClose();
      } else if (event.key === 'Tab') {
        const focusable = Array.from(panel.querySelectorAll<HTMLElement>(focusableSelector)).filter(
          (element) => element.tabIndex >= 0 && canFocus(element),
        );
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (!first || !last) return;
        if (event.shiftKey && doc.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && doc.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    };
    doc.addEventListener('keydown', handleKeyDown);
    const focusFrame = requestAnimationFrame(() => {
      if (topLayer(state) === layer) panel.focus();
    });

    return () => {
      cancelAnimationFrame(focusFrame);
      doc.removeEventListener('keydown', handleKeyDown);
      const wasTop = topModalLayer(state) === layer;
      state.layers.splice(state.layers.indexOf(layer), 1);
      // A lower layer can unmount first. Carry its trigger to any surviving
      // child rather than eventually focusing a removed panel.
      for (const remaining of state.layers) {
        if (remaining.previousFocus && panel.contains(remaining.previousFocus)) {
          remaining.previousFocus = layer.previousFocus;
        }
      }
      if (!state.layers.length) {
        body.removeAttribute('data-modal-scroll-lock');
        modalStates.delete(doc);
      }
      if (wasTop || !layer.root.isConnected) {
        const surviving = topModalLayer(state);
        const saved = layer.previousFocus;
        // React may detach the portal before cleanup, leaving focus on BODY.
        // Resolve the live survivor; do not disturb focus already inside it
        // when a detached lower layer unmounts.
        if (surviving) {
          if (wasTop || !surviving.panel.contains(doc.activeElement)) focusInside(surviving, saved);
        } else if (saved?.isConnected && canFocus(saved)) saved.focus();
      }
    };
  }, [open, panelRef, rootRef, preferredFocusRef, focusableSelector]);
}
